import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { PageLayout } from '../../components/layout';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { getKioskPayoutRequests, updateKioskPayoutRequestStatus } from '../../services/KioskBillingService';
import { getKiosks } from '../../services/KioskService';
import type { Kiosk } from '../../types/kiosk.type';
import type { KioskOwnerPayoutRequest, PayoutRequestStatus } from '../../types/kioskBilling.types';
import { formatDate } from '../../utils/dateFormatter';

const PAYOUT_STATUS_OPTIONS: Array<PayoutRequestStatus | ''> = ['', 'REQUESTED', 'PROCESSING', 'COMPLETED', 'REJECTED', 'FAILED'];

const money = (value: number, currencyCode = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((value || 0) / 100);
  } catch {
    return `${currencyCode} ${((value || 0) / 100).toFixed(2)}`;
  }
};

const OwnerPayoutRequestsPage: React.FC = () => {
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [payoutRequests, setPayoutRequests] = useState<KioskOwnerPayoutRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingKiosks, setLoadingKiosks] = useState(false);
  const [updatingPayoutRequestId, setUpdatingPayoutRequestId] = useState<number | null>(null);
  const [ownerEmailFilter, setOwnerEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<PayoutRequestStatus | ''>('');
  const [kioskFilter, setKioskFilter] = useState('');

  const loadKiosks = useCallback(async () => {
    setLoadingKiosks(true);
    try {
      const data = await getKiosks();
      setKiosks(data);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to load kiosks');
    } finally {
      setLoadingKiosks(false);
    }
  }, []);

  const loadPayoutRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getKioskPayoutRequests({
        ownerEmail: ownerEmailFilter.trim() || undefined,
        status: statusFilter || undefined,
        kioskId: kioskFilter || undefined,
      });
      setPayoutRequests(data);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to load payout requests');
    } finally {
      setLoading(false);
    }
  }, [kioskFilter, ownerEmailFilter, statusFilter]);

  useEffect(() => {
    void loadKiosks();
    void loadPayoutRequests();
  }, []);

  const kioskOptions = useMemo(
    () => kiosks.map((kiosk) => ({ value: kiosk.kioskId, label: `${kiosk.name || kiosk.kioskId} (${kiosk.kioskId})` })),
    [kiosks],
  );

  const updatePayoutStatus = async (requestId: number, status: PayoutRequestStatus) => {
    setUpdatingPayoutRequestId(requestId);
    try {
      const response = await updateKioskPayoutRequestStatus(requestId, { status });
      showSuccessToast(response.message || 'Payout request updated');
      await loadPayoutRequests();
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to update payout request');
    } finally {
      setUpdatingPayoutRequestId(null);
    }
  };

  return (
    <PageLayout
      title="Owner Payout Requests"
      subtitle="Review cash-out requests and update payout processing status"
    >
      <div className="space-y-6 p-6">
        <div className="rounded-2xl border border-white/60 bg-white/80 p-4 dark:border-white/10 dark:bg-slate-900/70">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Payout Request Queue</div>
              <div className="text-xs text-gray-600 dark:text-gray-300">Track bank transfers and Stripe payout updates</div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Kiosk</label>
                <select
                  value={kioskFilter}
                  onChange={(event) => setKioskFilter(event.target.value)}
                  disabled={loadingKiosks}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
                >
                  <option value="">All kiosks</option>
                  {kioskOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Owner Email</label>
                <input
                  value={ownerEmailFilter}
                  onChange={(event) => setOwnerEmailFilter(event.target.value)}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
                  placeholder="Filter by owner"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Status</label>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as PayoutRequestStatus | '')}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800"
                >
                  {PAYOUT_STATUS_OPTIONS.map((status) => (
                    <option key={status || 'all'} value={status}>
                      {status || 'All statuses'}
                    </option>
                  ))}
                </select>
              </div>
              <Button size="sm" variant="secondary" onClick={() => { void loadPayoutRequests(); }} loading={loading}>
                Filter
              </Button>
              <Button size="sm" variant="secondary" onClick={() => { void loadKiosks(); void loadPayoutRequests(); }} loading={loading || loadingKiosks}>
                Reload
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-white/10 dark:text-gray-400">
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Owner</th>
                  <th className="px-2 py-2">Kiosk</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2 text-right">Amount</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Stripe Transfer</th>
                  <th className="px-2 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && payoutRequests.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-2 py-6 text-center text-gray-500 dark:text-gray-400">
                      No payout requests found.
                    </td>
                  </tr>
                )}

                {payoutRequests.map((request) => (
                  <tr key={request.id} className="border-b border-gray-100 align-top dark:border-white/5">
                    <td className="px-2 py-3 whitespace-nowrap">{formatDate(request.createdAt)}</td>
                    <td className="px-2 py-3">{request.ownerEmail}</td>
                    <td className="px-2 py-3">{request.kioskPublicId || '-'}</td>
                    <td className="px-2 py-3">{request.requestType}</td>
                    <td className="px-2 py-3 text-right font-semibold">{money(request.amountCents)}</td>
                    <td className="px-2 py-3">
                      <Badge
                        label={request.status}
                        variant={request.status === 'COMPLETED' ? 'success' : request.status === 'FAILED' || request.status === 'REJECTED' ? 'error' : request.status === 'PROCESSING' ? 'warning' : 'default'}
                      />
                    </td>
                    <td className="px-2 py-3 text-xs text-gray-500 dark:text-gray-400">{request.stripeTransferId || '-'}</td>
                    <td className="px-2 py-3">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={request.status === 'PROCESSING' || request.status === 'COMPLETED'}
                          loading={updatingPayoutRequestId === request.id}
                          onClick={() => {
                            void updatePayoutStatus(request.id, 'PROCESSING');
                          }}
                        >
                          Processing
                        </Button>
                        <Button
                          size="sm"
                          disabled={request.status === 'COMPLETED'}
                          loading={updatingPayoutRequestId === request.id}
                          onClick={() => {
                            void updatePayoutStatus(request.id, 'COMPLETED');
                          }}
                        >
                          Mark Deposited
                        </Button>
                        <Button
                          size="sm"
                          variant="tertiary"
                          disabled={request.status === 'REJECTED'}
                          loading={updatingPayoutRequestId === request.id}
                          onClick={() => {
                            void updatePayoutStatus(request.id, 'REJECTED');
                          }}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default OwnerPayoutRequestsPage;
